/**
 * rip-routing-save: the RIP routing section's pure save helpers (leaf
 * validators and the save cleaner), kept out of `RipRoutingSection.tsx` so a
 * test can import them without loading the tRPC client.
 */
import {
  PLATEN_CODES,
  PRINT_FILE_CODE_REGEX,
  RIP_PROFILE_FOLDER_REGEX,
  type PlatenCode,
  type RipRoutingMap,
} from '@threadheads/shared';

export const isFolderValid = (v: string): boolean => RIP_PROFILE_FOLDER_REGEX.test(v.trim());
export const isPlatenValid = (v: string): boolean => (PLATEN_CODES as readonly string[]).includes(v);
/**
 * File-name codes are letters/digits only (they land in an on-disk filename).
 * A BLANK value is a first-class state meaning "use the built-in rule" — it is
 * handled by the callers, never by this predicate.
 */
export const isFileCodeValid = (v: string): boolean => PRINT_FILE_CODE_REGEX.test(v.trim());

/**
 * Save payload shape: `sizes` narrows to the closed `PlatenCode` set to match
 * the write-path zod (`z.enum(PLATEN_CODES)`); `colours` folders stay free-text.
 */
export type RipRoutingSaveEntry = {
  colours?: Record<string, string>;
  sizes?: Record<string, PlatenCode>;
  fileCodes?: Record<string, string>;
};
export type RipRoutingSaveMap = Record<string, RipRoutingSaveEntry>;

/**
 * Drop empty / invalid leaves and empty type-entries so a half-built map never
 * persists noise. Save is gated on `anyInvalid`, so this is also a belt-and-
 * braces guard that only valid values leave the client. Sizes keep any DTG
 * platen in `PLATEN_CODES` (10x12, 14x16, 16x18).
 *
 * Tested in `__tests__/RipRoutingSection.platens.test.ts`.
 */
export function cleanMapForSave(map: RipRoutingMap): RipRoutingSaveMap {
  const out: RipRoutingSaveMap = {};
  for (const [pt, entry] of Object.entries(map)) {
    const colours: Record<string, string> = {};
    for (const [code, folder] of Object.entries(entry.colours ?? {})) {
      const f = folder.trim();
      if (isFolderValid(f)) colours[code] = f;
    }
    const sizes: Record<string, PlatenCode> = {};
    for (const [code, platen] of Object.entries(entry.sizes ?? {})) {
      if (isPlatenValid(platen)) sizes[code] = platen as PlatenCode;
    }
    // A BLANK file code is intentional ("use the built-in rule"), so it is
    // simply not persisted — it must never block Save.
    const fileCodes: Record<string, string> = {};
    for (const [code, value] of Object.entries(entry.fileCodes ?? {})) {
      const v = value.trim();
      if (v.length > 0 && isFileCodeValid(v)) fileCodes[code] = v;
    }
    const hasColours = Object.keys(colours).length > 0;
    const hasSizes = Object.keys(sizes).length > 0;
    const hasFileCodes = Object.keys(fileCodes).length > 0;
    if (!hasColours && !hasSizes && !hasFileCodes) continue;
    const clean: RipRoutingSaveEntry = {};
    if (hasColours) clean.colours = colours;
    if (hasSizes) clean.sizes = sizes;
    if (hasFileCodes) clean.fileCodes = fileCodes;
    out[pt] = clean;
  }
  return out;
}
